import { assertNever } from '@kofno/piper';
import clsx from 'clsx';
import { ArrowDown, ArrowUp } from 'lucide-react';
import { observer } from 'mobx-react';
import { emptyFragment } from '../../../../../EmptyFragment';
import { T, TPlainTextKey } from '../../../../../Translations';
import ParticipantsV2Store from '../../Store';
import { SortColumn, SortDirection, sortDirectionFor } from '../../Store/Types';

interface Props {
  store: ParticipantsV2Store;
  column: SortColumn;
  label: TPlainTextKey;
  intercomTarget: string;
}

type AriaSort = 'ascending' | 'descending' | 'none';

const ariaSort = (direction: SortDirection): AriaSort => {
  switch (direction) {
    case 'asc':
      return 'ascending';
    case 'desc':
      return 'descending';
    default:
      return assertNever(direction);
  }
};

const DirectionIcon = ({ direction }: { direction: SortDirection }) => {
  switch (direction) {
    case 'asc':
      return <ArrowUp className="h-3.5 w-3.5" aria-hidden="true" />;
    case 'desc':
      return <ArrowDown className="h-3.5 w-3.5" aria-hidden="true" />;
    default:
      return assertNever(direction);
  }
};

const sortBy = (store: ParticipantsV2Store) => (column: SortColumn) => () => store.sortBy(column);

function SortHeader({ store, column, label, intercomTarget }: Props) {
  const direction = sortDirectionFor(store.query.sort, column);

  return (
    <th className="px-4 py-3 text-left" aria-sort={direction.map(ariaSort).getOrElseValue('none')}>
      <button
        type="button"
        onClick={sortBy(store)(column)}
        className={clsx(
          'inline-flex items-center gap-1 rounded transition-colors focus:outline-none focus:ring-2 focus:ring-ring',
          direction.map(() => 'text-primary').getOrElseValue('hover:text-primary'),
        )}
        style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-medium)' }}
        data-intercom-target={intercomTarget}
      >
        <T kind={label} />
        {direction.map((d) => <DirectionIcon direction={d} />).getOrElse(emptyFragment)}
      </button>
    </th>
  );
}

export default observer(SortHeader);
